import { useNavigate } from 'react-router-dom';
import { Building2, Network, Users, TrendingUp, ArrowRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

function HowItWorksPage() {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      icon: Building2,
      title: 'List Your Resort',
      description: 'Create a detailed profile for your property with photos, amenities, and unique features. Our team helps you showcase what makes your resort special.',
      details: [
        'Professional property listing',
        'Photo gallery with unlimited images',
        'Detailed amenities and features',
        'Unique selling points highlighted'
      ]
    },
    {
      number: 2,
      icon: Network,
      title: 'Get Featured Across Network',
      description: 'Your property is automatically promoted across our partner network of 500+ resorts, reaching thousands of potential guests every month.',
      details: [
        'Featured in partner newsletters',
        'Cross-promotion on social media',
        'Listed in resort directory',
        'Highlighted to matching guest profiles'
      ]
    },
    {
      number: 3,
      icon: Users,
      title: 'Receive Referral Guests',
      description: 'Partner resorts refer their guests to you when they are looking for their next destination. Build a network of quality referrals.',
      details: [
        'Qualified guest referrals',
        'Matched based on preferences',
        'Direct booking communication',
        'No third-party interference'
      ]
    },
    {
      number: 4,
      icon: TrendingUp,
      title: 'Grow Bookings Without High Commissions',
      description: 'Watch your direct bookings grow by an average of 45% while paying only a simple monthly subscription. No per-booking commissions ever.',
      details: [
        'Zero commission on bookings',
        'Predictable monthly costs',
        'Keep 100% of your revenue',
        'Cancel anytime, no lock-in'
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-50 to-white" data-testid="hiw-hero">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">How Travelodges Works</h1>
          <p className="text-xl text-gray-600 mb-8">
            A simple 4-step process to start growing your resort bookings through collaboration
          </p>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white" data-testid="steps-section">
        <div className="max-w-5xl mx-auto">
          {steps.map((step, index) => (
            <div 
              key={step.number} 
              className={`mb-16 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}
              data-testid={`step-${step.number}`}
            >
              <Card className="border-2 hover:border-emerald-500 transition-all">
                <CardContent className="p-8">
                  <div className="flex flex-col md:flex-row items-start gap-6">
                    <div className="flex-shrink-0">
                      <div className="w-20 h-20 bg-emerald-600 rounded-full flex items-center justify-center">
                        <step.icon className="h-10 w-10 text-white" />
                      </div>
                      <div className="mt-4 text-center">
                        <div className="text-3xl font-bold text-emerald-600">Step {step.number}</div>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-900 mb-3">{step.title}</h3>
                      <p className="text-gray-600 mb-4 text-lg">{step.description}</p>
                      <ul className="space-y-2">
                        {step.details.map((detail, idx) => (
                          <li key={idx} className="flex items-start space-x-2">
                            <Check className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                            <span className="text-gray-700">{detail}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      </section>

      {/* Success Story Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-emerald-50" data-testid="success-story">
        <div className="max-w-4xl mx-auto">
          <Card className="bg-white">
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">See Real Results</h2>
                <p className="text-gray-600">Here's what happens after you join Travelodges</p>
              </div>
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-600 mb-2">Week 1</div>
                  <p className="text-gray-700">Profile live & featured across network</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-600 mb-2">Month 1</div>
                  <p className="text-gray-700">First referral guests start arriving</p>
                </div>
                <div className="text-center">
                  <div className="text-4xl font-bold text-emerald-600 mb-2">Month 6</div>
                  <p className="text-gray-700">45% average increase in bookings</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-emerald-600 to-emerald-800" data-testid="hiw-cta">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-emerald-100 mb-10">
            Join 500+ resorts growing their business through collaboration
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-10 py-6"
              onClick={() => navigate('/join')}
            >
              List Your Property
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              className="bg-transparent border-2 border-white text-white hover:bg-white/10 text-lg px-10 py-6"
              onClick={() => navigate('/pricing')}
            >
              View Pricing
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default HowItWorksPage;